import { DateTimeFormatter, ZonedDateTime, ZoneId } from "@js-joda/core";
import { Alert, Badge, Col, Row, Spinner } from "react-bootstrap";
import {
	BILLING_ADDON_INFO,
	BillingAddonSlug,
	BillingInterval,
	type BillingPlanSlug,
	type PlanDto,
} from "shared";
import { PricingCard, PricingCardList } from "../billing/PricingCard";
import "@js-joda/timezone";
import { Locale } from "@js-joda/locale_en-us";
import { useCallback } from "react";
import { InvoiceTable } from "../billing/InvoiceTable";
import {
	useBillingActions,
	useBillingEntitlements,
	useBillingPlanPaidCheck,
	usePlans,
	useSubscription,
} from "../billing/useSubscription";
import { Button } from "../components/Button";

function formatDate(isoString: string) {
	const userTimeZone = ZoneId.of(
		Intl.DateTimeFormat().resolvedOptions().timeZone,
	); // e.g., "America/New_York"
	const utcDateTime = ZonedDateTime.parse(isoString);
	const localDateTime = utcDateTime.withZoneSameInstant(userTimeZone);
	const localDate = localDateTime.toLocalDate();
	const formatter = DateTimeFormatter.ofPattern("dd MMM uuuu").withLocale(
		Locale.ENGLISH,
	);
	return localDate.format(formatter);
}

export const TabBilling = () => {
	const { subscriptions } = useSubscription();
	const { data: entitlements } = useBillingEntitlements();
	const isPaid = useBillingPlanPaidCheck();
	const { plans } = usePlans();
	const { upgrade, cancel, openStripePortal, addAddon, removeAddon } =
		useBillingActions();
	const getMonthlyPrice = useCallback(
		(slug: BillingPlanSlug) => {
			const plan = plans.find((x) => x.slug === slug);

			if (!plan) {
				return "Unknown";
			}

			const price = plan.prices.find(
				(x) => x.interval === BillingInterval.MONTHLY,
			);

			if (!price) {
				return "FREE";
			}

			return `$${(price.amount / 100).toFixed(2)} USD`;
		},
		[plans],
	);

	const handleUpgrade = async (plan: PlanDto) => {
		await upgrade.mutateAsync({
			plan: plan.slug,
			interval: BillingInterval.MONTHLY,
		});
	};

	return (
		<>
			<h2 className="mb-4">Billing</h2>
			<Row>
				<Col md={3}>
					<div className="stretch-col">
						<h3>Plan & Usage</h3>
						{subscriptions.data.map((subscription) => (
							<PricingCard
								key={subscription.plan.slug}
								header="Current plan"
								title={subscription.plan.name}
								content={subscription.plan.description}
							>
								<span className="pricing-card-frequency">
									{subscription.interval}
								</span>
								<div className="pricing-card-price">
									Price: {getMonthlyPrice(subscription.plan.slug)}
								</div>
								{subscription.trialEnd ? (
									<div className="mt-2">
										<span className="pricing-card-frequency">
											Your Trial ends on
										</span>
										<div className="pricing-card-price">
											{formatDate(subscription.trialEnd)}
										</div>
									</div>
								) : null}

								{subscription.to ? (
									<div className="mt-2">
										<span className="pricing-card-frequency">
											Valid until{" "}
											{subscription.autoRenewal ? " / Renew on" : ""}
										</span>
										<div className="pricing-card-price">
											{formatDate(subscription.to)}
										</div>
									</div>
								) : null}

								{subscription.canCancel && (
									<Button
										loading={cancel.isPending}
										className="mt-2"
										full
										variant="danger"
										onClick={() => cancel.mutateAsync()}
									>
										Cancel
									</Button>
								)}
								{subscription.isCancelled ? (
									<Alert className="mt-2" variant="warning">
										Your subscription is cancelled and will not be renewed
										automatically.
									</Alert>
								) : null}
							</PricingCard>
						))}
					</div>
				</Col>
				<Col md={9}>
					<div className="stretch-col">
						<h3>Subscription Plans</h3>
						<PricingCardList>
							{plans
								.filter((plan) => plan.isVisible)
								.map((plan) => (
									<PricingCard
										key={plan.slug}
										header="Subscription"
										title={plan.name}
										content={
											<>
												{plan.trialDays ? (
													<div className="py-1">
														<Badge bg="primary">
															Eligible for free {plan.trialDays} days trial
														</Badge>
													</div>
												) : null}
												{plan.description}
											</>
										}
									>
										{/*<span className="pricing-card-frequency">*/}
										{/*	{subscription.data.interval}*/}
										{/*</span>*/}

										<div className="pricing-card-price">
											Price: {getMonthlyPrice(plan.slug)}
										</div>
										<Button
											full
											className="mt-2"
											disabled={!plan.canActivate}
											onClick={() => handleUpgrade(plan)}
										>
											Activate
										</Button>
									</PricingCard>
								))}
						</PricingCardList>

						<h3 className="mt-4">X add-ons</h3>
						{!isPaid && (
							<Alert variant="info">
								Production needs a paid plan first. In development, Activate
								provisions a mock plan automatically so you can test X add-ons.
							</Alert>
						)}
						<PricingCardList>
							{(
								[
									BillingAddonSlug.X_INTEGRATION,
									BillingAddonSlug.X_LINK_POSTING,
								] as const
							).map((slug) => {
								const info = BILLING_ADDON_INFO[slug];
								const active =
									slug === BillingAddonSlug.X_INTEGRATION
										? entitlements.xIntegration
										: entitlements.xLinkPosting;
								const canActivateLink =
									slug !== BillingAddonSlug.X_LINK_POSTING ||
									entitlements.xIntegration;
								return (
									<PricingCard
										key={slug}
										header="Add-on"
										title={info.name}
										content={info.description}
									>
										<div className="pricing-card-price">
											${info.suggestedMonthlyUsd.toFixed(2)} USD / month
										</div>
										{active ? (
											<>
												<Badge bg="success" className="mt-2">
													Active
												</Badge>
												<Button
													full
													className="mt-2"
													variant="danger"
													loading={removeAddon.isPending}
													onClick={() => removeAddon.mutateAsync(slug)}
												>
													Deactivate
												</Button>
											</>
										) : (
											<Button
												full
												className="mt-2"
												loading={addAddon.isPending}
												disabled={!canActivateLink}
												onClick={() => addAddon.mutateAsync(slug)}
											>
												{slug === BillingAddonSlug.X_LINK_POSTING &&
												!entitlements.xIntegration
													? "Requires X Integration"
													: "Activate"}
											</Button>
										)}
									</PricingCard>
								);
							})}
						</PricingCardList>
					</div>
				</Col>
			</Row>
			<Row className="my-3">
				<Col className="d-flex align-items-center">
					<button
						type="button"
						onClick={() => {
							if (!openStripePortal.isPending) {
								openStripePortal.mutateAsync();
							}
						}}
						style={{
							opacity: openStripePortal.isPending ? 0.7 : 1,
							pointerEvents: openStripePortal.isPending ? "none" : "auto",
							background: "none",
							border: "none",
							padding: 0,
							color: "inherit",
							textDecoration: "underline",
							cursor: "pointer",
						}}
						className="d-flex align-items-center"
					>
						Manage payment details
						{openStripePortal.isPending && (
							<Spinner animation="border" size="sm" className="ms-2" />
						)}
					</button>
				</Col>
			</Row>
			<Row className="my-4">
				<Col>
					<h1 className="mb-3">Invoices</h1>
					<InvoiceTable />
				</Col>
			</Row>
		</>
	);
};
